import * as React from "react";
import {Box, Button, Card, CardContent, Chip, CircularProgress, Divider, IconButton, Menu, MenuItem, Stack, Typography} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {useTranslation} from "react-i18next";
import type {Branch, Contract} from "../../../services/payrollService";
import {formatPeriodLabel} from "../../../utils/periodLabel";
import {formatShortDate, formatShortDateRange} from "./periodFormat";

interface PayrollPeriodStatusPanelProps {
    selectedPeriod: string | null;
    selectedPeriodValue: string | null;
    isLockedState: boolean;
    contracts: Contract[];
    contractsLoading: boolean;
    branches: Branch[];
    evidenceTotal: number;
    evidencePending: number;
    evidenceLoading: boolean;
    calculationAvailable: boolean;
    contractsLastSyncedAt: Date | null;
    handleRefreshContracts: () => void;
    snapshotCreating: boolean;
    handleGenerateSnapshot: () => void;
    lockButtonText: string;
    onLockUnlockClick: () => void;
    onDeleteSnapshotClick: () => void;
}

interface MiniStatProps {
    icon: React.ReactElement;
    value: React.ReactNode;
    label: string;
}

function MiniStat({icon, value, label}: Readonly<MiniStatProps>): React.ReactElement {
    return (
        <Card variant="outlined" sx={{borderRadius: 1, flex: 1}}>
            <CardContent sx={{display: "flex", alignItems: "center", gap: 1.5, "&:last-child": {pb: 2}}}>
                <Box sx={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    width: 40, height: 40, borderRadius: 1,
                    backgroundColor: "secondary.light", color: "secondary.dark", flexShrink: 0,
                }}>
                    {icon}
                </Box>
                <Box sx={{textAlign: "left", minWidth: 0}}>
                    <Typography variant="h6" fontWeight={700} noWrap>{value}</Typography>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                </Box>
            </CardContent>
        </Card>
    );
}

interface ChecklistRowProps {
    state: "done" | "warning" | "pending";
    text: string;
}

function ChecklistRow({state, text}: Readonly<ChecklistRowProps>): React.ReactElement {
    const icon = state === "done"
        ? <CheckCircleIcon fontSize="small" sx={{color: "success.main"}}/>
        : state === "warning"
            ? <AccessTimeIcon fontSize="small" sx={{color: "warning.main"}}/>
            : <RemoveCircleOutlineIcon fontSize="small" sx={{color: "text.disabled"}}/>;
    const color = state === "done" ? "success.main" : state === "warning" ? "warning.main" : "text.secondary";
    return (
        <Stack direction="row" alignItems="center" spacing={1} sx={{py: 0.5}}>
            {icon}
            <Typography variant="body2" sx={{color}}>{text}</Typography>
        </Stack>
    );
}

export default function PayrollPeriodStatusPanel({
    selectedPeriod,
    selectedPeriodValue,
    isLockedState,
    contracts,
    contractsLoading,
    branches,
    evidenceTotal,
    evidencePending,
    evidenceLoading,
    calculationAvailable,
    contractsLastSyncedAt,
    handleRefreshContracts,
    snapshotCreating,
    handleGenerateSnapshot,
    lockButtonText,
    onLockUnlockClick,
    onDeleteSnapshotClick,
}: Readonly<PayrollPeriodStatusPanelProps>): React.ReactElement {
    const {t} = useTranslation();
    const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);

    if (!selectedPeriod) {
        return (
            <Card variant="outlined" sx={{borderRadius: 1, height: "100%"}}>
                <CardContent sx={{textAlign: "center", py: 6}}>
                    <Typography variant="body1" fontWeight={600}>
                        {t('payroll.period.noPeriodSelected.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('payroll.period.noPeriodSelected.description')}
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    const evidenceApproved = Math.max(0, evidenceTotal - evidencePending);
    const contractsDone = contracts.length > 0;

    return (
        <Card variant="outlined" sx={{borderRadius: 1, height: "100%"}}>
            <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mb: 0.5}}>
                    <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                        <Typography variant="h6" fontWeight={700}>
                            {formatPeriodLabel(t, selectedPeriod, selectedPeriodValue)}
                        </Typography>
                        <Chip
                            label={isLockedState ? t('dashboard.wizard.status.completed') : t('dashboard.wizard.status.inProgress')}
                            size="small"
                            color={isLockedState ? "success" : "warning"}
                            variant="outlined"
                        />
                    </Stack>
                    <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="period actions">
                        <MoreVertIcon fontSize="small"/>
                    </IconButton>
                    <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
                        <MenuItem
                            disabled={snapshotCreating || !selectedPeriod || isLockedState}
                            onClick={() => {
                                setMenuAnchor(null);
                                handleGenerateSnapshot();
                            }}
                        >
                            {snapshotCreating ? t('payroll.period.generating') : t('payroll.period.generateSnapshot')}
                        </MenuItem>
                        <MenuItem
                            disabled={!selectedPeriod}
                            onClick={() => {
                                setMenuAnchor(null);
                                onLockUnlockClick();
                            }}
                        >
                            {lockButtonText}
                        </MenuItem>
                        <MenuItem
                            disabled={!selectedPeriod || isLockedState}
                            onClick={() => {
                                setMenuAnchor(null);
                                onDeleteSnapshotClick();
                            }}
                            sx={{color: "error.main"}}
                        >
                            {t('payroll.period.deleteSnapshot')}
                        </MenuItem>
                    </Menu>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    {formatShortDateRange(selectedPeriod)}
                </Typography>

                <Stack direction={{xs: "column", sm: "row"}} spacing={1.5} sx={{mb: 3}}>
                    <MiniStat
                        icon={<DescriptionOutlinedIcon/>}
                        value={contractsLoading ? '…' : contracts.length}
                        label={t('payroll.period.stats.contracts')}
                    />
                    <MiniStat
                        icon={<AccountTreeOutlinedIcon/>}
                        value={branches.length}
                        label={t('payroll.period.stats.branches')}
                    />
                    <MiniStat
                        icon={<BarChartOutlinedIcon/>}
                        value={evidenceLoading ? '…' : `${evidenceApproved} / ${evidenceTotal}`}
                        label={t('payroll.period.stats.evidenceApproved')}
                    />
                </Stack>

                <Stack direction={{xs: "column", sm: "row"}} justifyContent="space-between" alignItems={{xs: "flex-start", sm: "center"}} spacing={1.5} sx={{mb: 1}}>
                    <Box sx={{textAlign: "left"}}>
                        <Typography variant="subtitle1" fontWeight={700}>
                            {t('payroll.period.contractsSection.title')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('payroll.period.contractsSection.subtitle')}
                        </Typography>
                    </Box>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        {contractsLastSyncedAt && (
                            <Typography variant="caption" color="text.secondary" noWrap>
                                {t('payroll.period.contractsSection.lastSynced', {date: formatShortDate(contractsLastSyncedAt)})}
                            </Typography>
                        )}
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={contractsLoading ? <CircularProgress size={14}/> : <RefreshIcon/>}
                            onClick={handleRefreshContracts}
                            disabled={contractsLoading}
                        >
                            {t('payroll.period.contractsSection.refresh')}
                        </Button>
                    </Stack>
                </Stack>

                <Divider sx={{my: 1.5}}/>

                <ChecklistRow
                    state={contractsDone ? "done" : "pending"}
                    text={contractsDone ? t('payroll.period.contractsSection.included') : t('payroll.period.table.noContractsFound')}
                />
                <ChecklistRow
                    state={evidenceTotal === 0 ? "pending" : evidencePending > 0 ? "warning" : "done"}
                    text={
                        evidenceTotal === 0
                            ? t('payroll.period.contractsSection.evidenceNone')
                            : evidencePending > 0
                                ? t('payroll.period.contractsSection.evidencePending', {count: evidencePending})
                                : t('payroll.period.contractsSection.evidenceApproved')
                    }
                />
                <ChecklistRow
                    state={calculationAvailable ? "done" : "pending"}
                    text={calculationAvailable
                        ? t('payroll.period.contractsSection.calculationDone')
                        : t('payroll.period.contractsSection.calculationPending')}
                />
            </CardContent>
        </Card>
    );
}
